import { Outlet, useLocation, useParams } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { visibleModelSections } from '@/features/projects/utils/modelSections';
import { TabNav } from '@/shared/components/ui/TabNav/TabNav';
import { toProjectId } from '@/shared/types/common.types';
import { useModelKind } from '../hooks/useProjectDetails';
import { useWorkspaceProject } from '../hooks/useWorkspaceProjects';

/** Which model section a page below the model belongs to. */
function sectionOfPath(rest: string): string {
  const first = rest.split('/')[0] ?? '';
  return ['families', 'timeline', 'audits'].includes(first) ? first : 'overview';
}

/**
 * The route element around a model. The model page draws its own section tabs; the pages it
 * opens (a family, a snapshot diff, an audit run) are routes below it, and this frame puts the
 * same tabs above them so navigating into one never drops the navigation.
 */
export function ModelFrame(): React.JSX.Element {
  const { projectId = '', modelId = '' } = useParams();
  const { pathname } = useLocation();
  const base = ROUTES.projectModel(projectId, Number(modelId));
  const rest = pathname.startsWith(base) ? pathname.slice(base.length).replace(/^\//, '') : '';
  const project = useWorkspaceProject(toProjectId(projectId), projectId !== '');
  const canManage = project.data?.myRole === 'ProjectAdmin';
  const kind = useModelKind(projectId === '' ? null : projectId, Number(modelId));

  if (rest === '') return <Outlet />;

  const current = sectionOfPath(rest);
  return (
    <div className="model-frame">
      <TabNav
        label="Model sections"
        items={visibleModelSections(canManage, kind).map((section) => ({
          to: `${base}?tab=${section.id}`,
          label: section.label,
          current: section.id === current,
        }))}
      />
      <Outlet />
    </div>
  );
}
